import { v4 as uuid } from 'uuid';
import { lensIndex, over, remove } from 'ramda';
import React, { useCallback, useRef } from 'react';
import Dropdown, { type OptionOf } from '~/components/molecule/Dropdown';
import EditableText from '~/components/organism/EditableText';
import {
  FormBuilder_Label_Intl,
  FormBuilder_ScreenNode,
  FormBuilder_ScreenNode_Block,
  FormData_TypographyType,
  Maybe,
} from '~/graphql/types';
import InputForType from './components/InputForType';
import JustificationContainer from '~/components/atom/JustificationContainer';
import ScreenPreview from './components/ScreenPreview';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import styled, { css } from 'styled-components';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import type { EditProps } from '../..';
import { useRecoilState, type SetterOrUpdater } from 'recoil';
import InputWithOptions from './components/InputWithOptions';
import Image from './components/Image';
import { nodeById } from '../../../../state/nodesAndEvents';
import isInputTypeBlock from '../../../../utils/isInputTypeBlock';
import isSelectTypeBlock from '../../../../utils/isSelectTypeBlock';
import SelectTypography from './components/SelectTypography';
import AddMarkdown from './components/RichText';
import { blockLabelMap } from './constants';
import DraggableItem from '~/components/atom/DraggableItem';
import DnDContext from '~/contexts/DnDContext';

export type ScreenFormState = FormBuilder_ScreenNode;

export type Props = EditProps<ScreenFormState>;

const EditNodeScreen: React.FCC<Props> = ({ id, onSave, onCancel }) => {
  const [node, setFormState] = useRecoilState(nodeById(id)) as [
    Maybe<FormBuilder_ScreenNode>,
    SetterOrUpdater<FormBuilder_ScreenNode | null>,
  ];

  // We keep track of the shape we got initially so we can restore it on cancel.
  const initialState = useRef<FormBuilder_ScreenNode>({
    id,
    blocks: node?.blocks ?? [],
    title: node?.title ?? '',
    __typename: 'FormBuilder_ScreenNode',
    defaultNext: node?.defaultNext,
  });

  const onBlockMove = useCallback((dragIndex: number, hoverIndex: number) => {
    setFormState(prev => {
      if (!prev) return prev;
      const updatedBlocks = [...prev.blocks];
      const [draggedItem] = updatedBlocks.splice(dragIndex, 1);
      updatedBlocks.splice(hoverIndex, 0, draggedItem);

      return { ...prev, blocks: updatedBlocks };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <JustificationContainer>
      <JustificationContainer
        direction="column"
        gap="m"
        padding={['xl', null, 'xl', 'xl']}
        style={{ flexGrow: 1 }}
      >
        <InnerContainer
          direction="column"
          justification="space-between"
          width="100%"
          height="100%"
        >
          <EditableText
            as="h1"
            text={node?.title}
            onSave={title =>
              setFormState(prev => (!prev ? prev : { ...prev, title }))
            }
            margin={['l', null, 'l', null]}
          />
          <DnDContext>
            <JustificationContainer
              direction="column"
              gap="m"
              width="100%"
              margin={[
                null,
                null,
                node?.blocks.length === 0 ? null : 'm',
                null,
              ]}
            >
              {node?.blocks.map((block, index) => (
                <DraggableItem
                  key={block.key}
                  id={index}
                  index={index}
                  onMove={onBlockMove}
                  style={{ width: '100%' }}
                >
                  <EditableBlock
                    key={block.key}
                    block={block}
                    nodeId={id}
                    onDelete={() => {
                      setFormState(prev => {
                        if (!prev) return prev;

                        const nextBlocks = remove(index, 1, prev.blocks);

                        return {
                          ...prev,
                          blocks: nextBlocks,
                        };
                      });
                    }}
                    onChange={update => {
                      setFormState(prev => {
                        if (!prev) return prev;

                        const lens =
                          lensIndex<FormBuilder_ScreenNode_Block>(index);
                        const nextBlocks = over(
                          lens,
                          block => ({ ...block, ...update }),
                          prev.blocks,
                        );

                        return {
                          ...prev,
                          blocks: nextBlocks,
                        };
                      });
                    }}
                  />
                </DraggableItem>
              ))}
            </JustificationContainer>
          </DnDContext>
          <Dropdown
            width="100%"
            label="Blok toevoegen"
            options={blockOptions}
            onChange={selected => {
              const payload: FormBuilder_ScreenNode_Block['__typename'] =
                selected.option.payload;

              setFormState(prev =>
                !prev
                  ? prev
                  : {
                      ...prev,
                      blocks: [...prev.blocks, getEmptyBlock(payload)],
                    },
              );
            }}
          />
        </InnerContainer>
        <JustificationContainer
          justification="space-between"
          align="space-between"
          width="100%"
          padding={[null, 'xl', null, null]}
        >
          <TextButton
            label="Afbreken"
            appearance="danger"
            padding={[null]}
            onClick={() => {
              setFormState(prev => (!prev ? prev : initialState.current));
              onCancel();
            }}
          />
          <Button
            label="Opslaan"
            icon="save"
            onClick={() => {
              if (node) {
                onSave(node);
              }
            }}
          />
        </JustificationContainer>
      </JustificationContainer>
      {node && <ScreenPreview node={node} />}
    </JustificationContainer>
  );
};

const getEmptyBlock = (
  type: FormBuilder_ScreenNode_Block['__typename'],
): FormBuilder_ScreenNode_Block => {
  const key = uuid();
  const emptyLabel: FormBuilder_Label_Intl = {
    __typename: 'FormBuilder_Label_Intl',
    en: `New ${blockLabelMap[type]}`,
    nl: `Nieuw ${blockLabelMap[type]}`,
  };

  switch (type) {
    case 'FormData_Input_Email':
    case 'FormData_Input_Integer':
    case 'FormData_Input_Multiline':
    case 'FormData_Input_Text':
      return {
        __typename: type,
        label: emptyLabel,
        key,
      };

    case 'FormData_Select_Dropdown':
    case 'FormData_Select_MultiButton':
    case 'FormData_Select_Radio':
      return { __typename: type, key, options: '', label: emptyLabel };

    case 'FormData_UI_Image':
      return {
        __typename: 'FormData_UI_Image',
        alt: emptyLabel,
        image: { __typename: 'DHImage', s3key: '', url: '' },
        key,
        width: 800,
      };
    case 'FormData_UI_RichText':
      return {
        __typename: 'FormData_UI_RichText',
        content: emptyLabel,
        key,
      };
    case 'FormData_UI_Typography':
      return {
        __typename: 'FormData_UI_Typography',
        content: emptyLabel,
        key,
        type: FormData_TypographyType.Heading1,
      };
  }
};

const blockOptions: Array<
  OptionOf<FormBuilder_ScreenNode_Block['__typename']>
> = [
  {
    payload: 'FormData_Input_Email',
    key: 'FormData_Input_Email',
    label: blockLabelMap['FormData_Input_Email'],
  },
  {
    payload: 'FormData_Input_Integer',
    key: 'FormData_Input_Integer',
    label: blockLabelMap['FormData_Input_Integer'],
  },
  {
    payload: 'FormData_Input_Multiline',
    key: 'FormData_Input_Multiline',
    label: blockLabelMap['FormData_Input_Multiline'],
  },
  {
    payload: 'FormData_Input_Text',
    key: 'FormData_Input_Text',
    label: blockLabelMap['FormData_Input_Text'],
  },
  {
    payload: 'FormData_Select_Dropdown',
    key: 'FormData_Select_Dropdown',
    label: blockLabelMap['FormData_Select_Dropdown'],
  },
  {
    payload: 'FormData_Select_MultiButton',
    key: 'FormData_Select_MultiButton',
    label: blockLabelMap['FormData_Select_MultiButton'],
  },
  {
    payload: 'FormData_Select_Radio',
    key: 'FormData_Select_Radio',
    label: blockLabelMap['FormData_Select_Radio'],
  },
  {
    payload: 'FormData_UI_RichText',
    key: 'FormData_UI_RichText',
    label: blockLabelMap['FormData_UI_RichText'],
  },
  {
    payload: 'FormData_UI_Image',
    key: 'FormData_UI_Image',
    label: blockLabelMap['FormData_UI_Image'],
  },
  {
    payload: 'FormData_UI_Typography',
    key: 'FormData_UI_Typography',
    label: blockLabelMap['FormData_UI_Typography'],
  },
];

const EditableBlock: React.FC<{
  nodeId: string;
  block: FormBuilder_ScreenNode_Block;
  onChange: (block: FormBuilder_ScreenNode_Block) => void;
  onDelete: () => void;
}> = ({ block, onChange, onDelete, nodeId }) => {
  if (isInputTypeBlock(block)) {
    return (
      <InputForType
        onChange={onChange}
        onDelete={onDelete}
        nodeId={nodeId}
        block={block}
      />
    );
  }
  if (isSelectTypeBlock(block)) {
    return (
      <InputWithOptions
        onChange={onChange}
        onDelete={onDelete}
        block={block}
        nodeId={nodeId}
      />
    );
  }

  switch (block.__typename) {
    case 'FormData_UI_RichText':
      return (
        <AddMarkdown
          onChange={onChange}
          onDelete={onDelete}
          block={block}
          nodeId={nodeId}
        />
      );
    case 'FormData_UI_Typography':
      return (
        <SelectTypography
          onChange={onChange}
          onDelete={onDelete}
          block={block}
          nodeId={nodeId}
        />
      );
    case 'FormData_UI_Image': {
      return (
        <Image
          onChange={onChange}
          onDelete={onDelete}
          block={block}
          nodeId={nodeId}
        />
      );
    }
  }

  return null;
};

const InnerContainer = styled(JustificationContainer)(
  ({ theme }) => css`
    height: 100%;
    max-height: 680px;
    padding-right: ${theme.space('l')};
    overflow-y: scroll;

    ${scrollBarStyles}
  `,
);

export default EditNodeScreen;
